import React, { useMemo, useState } from 'react';
import { CalendarClock, Filter, ShieldAlert } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useConstruction } from '../../context/ConstructionContext';
import { useModuleSettings } from '../../context/ModuleSettingsContext';
import StatCard from '../StatCard';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import Button from '../common/Button';
import { t } from '../../i18n';

const priorityTone = {
  alta: 'danger',
  media: 'warning',
  baja: 'info'
};

const DevelopmentPermitAlerts = () => {
  const { renewablePermitAlerts: permitAlerts, renewableProjectMap, parks } = useAppContext();
  const { constructionAlerts } = useConstruction();
  const { isModuleActive } = useModuleSettings();
  const constructionActive = isModuleActive('construction-module');
  const [filter, setFilter] = useState('todas');
  const [source, setSource] = useState('todas');

  // Motor de alertas unificado: permisos/PAS de Desarrollo + alertas de obra.
  const renewablePermitAlerts = useMemo(() => {
    const development = permitAlerts.map((alert) => ({
      ...alert,
      source: 'desarrollo',
      projectName: renewableProjectMap[alert.projectId]?.name || '-'
    }));
    if (!constructionActive) return development;
    const construction = constructionAlerts.map((alert) => ({
      ...alert,
      source: 'construccion',
      title: t(alert.title, ...(alert.titleArgs || [])),
      description: t(alert.description || '', ...(alert.descriptionArgs || [])),
      dueDate: alert.dueDate || null,
      projectName: (parks || []).find((park) => park.id === alert.parkId)?.name || '-'
    }));
    return [...construction, ...development];
  }, [permitAlerts, constructionAlerts, constructionActive, renewableProjectMap, parks]);

  const filteredAlerts = useMemo(() => {
    const bySource = source === 'todas' ? renewablePermitAlerts : renewablePermitAlerts.filter((item) => item.source === source);
    if (filter === 'todas') return bySource;
    return bySource.filter((item) => item.priority === filter);
  }, [filter, source, renewablePermitAlerts]);

  const summary = useMemo(() => {
    const active = renewablePermitAlerts.length;
    const high = renewablePermitAlerts.filter((item) => item.priority === 'alta').length;
    const medium = renewablePermitAlerts.filter((item) => item.priority === 'media').length;
    const low = renewablePermitAlerts.filter((item) => item.priority === 'baja').length;
    const dueSoon = renewablePermitAlerts.filter((item) => item.overdueDays <= 14).length;
    return { active, high, medium, low, dueSoon };
  }, [renewablePermitAlerts]);

  const rows = useMemo(
    () => filteredAlerts.map((alert) => ({
      ...alert,
      sourceLabel: alert.source === 'construccion' ? 'Construcción' : 'Permisos/PAS'
    })),
    [filteredAlerts]
  );

  const columns = [
    {
      key: 'title',
      label: t('Alerta'),
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-semibold text-slate-900">{t(value)}</p>
          <p className="text-slate-500 text-xs mt-0.5">{t(row.description)}</p>
        </div>
      )
    },
    { key: 'projectName', label: t('Proyecto'), sortable: true },
    {
      key: 'sourceLabel',
      label: t('Origen'),
      sortable: true,
      render: (value, row) => <Badge tone={row.source === 'construccion' ? 'info' : 'neutral'}>{t(value)}</Badge>
    },
    { key: 'responsible', label: t('Responsable'), sortable: true },
    {
      key: 'dueDate',
      label: t('Vencimiento'),
      sortable: true,
      align: 'right',
      render: (value) => value || '-'
    },
    {
      key: 'overdueDays',
      label: t('Vencido hace (dias)'),
      sortable: true,
      align: 'right'
    },
    {
      key: 'priority',
      label: t('Prioridad'),
      sortable: true,
      render: (value) => <Badge tone={priorityTone[value] || 'neutral'}>{t(value)}</Badge>
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('Alertas criticas y recordatorios')}</h1>
        <p className="text-slate-600 text-sm">{t(
          'Seguimiento automatico de vencimientos de PAS, hitos ambientales y compromisos legales.'
        )}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard title={t('Alertas activas')} value={summary.active} icon={ShieldAlert} />
        <StatCard title={t('Alta criticidad')} value={summary.high} icon={ShieldAlert} />
        <StatCard title={t('Mediana criticidad')} value={summary.medium} icon={ShieldAlert} />
        <StatCard title={t('Baja criticidad')} value={summary.low} icon={ShieldAlert} />
        <StatCard title={t('Vencen <= 14 dias')} value={summary.dueSoon} icon={CalendarClock} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-slate-600 text-sm">
          <Filter className="w-4 h-4 text-neutral-400" /> {t('Filtra por criticidad')}</span>
        <Button size="sm" variant={filter === 'todas' ? 'primary' : 'secondary'} onClick={() => setFilter('todas')}>{t('Todas')}</Button>
        <Button size="sm" variant={filter === 'alta' ? 'primary' : 'secondary'} onClick={() => setFilter('alta')}>{t('Prioridad alta')}</Button>
        <Button size="sm" variant={filter === 'media' ? 'primary' : 'secondary'} onClick={() => setFilter('media')}>{t('Prioridad media')}</Button>
        <Button size="sm" variant={filter === 'baja' ? 'primary' : 'secondary'} onClick={() => setFilter('baja')}>{t('Prioridad baja')}</Button>
        {constructionActive && (
          <>
            <span className="mx-2 h-5 border-l border-neutral-200" />
            <Button size="sm" variant={source === 'todas' ? 'primary' : 'secondary'} onClick={() => setSource('todas')}>{t('Todos los orígenes')}</Button>
            <Button size="sm" variant={source === 'desarrollo' ? 'primary' : 'secondary'} onClick={() => setSource('desarrollo')}>{t('Permisos/PAS')}</Button>
            <Button size="sm" variant={source === 'construccion' ? 'primary' : 'secondary'} onClick={() => setSource('construccion')}>{t('Construcción')}</Button>
          </>
        )}
      </div>

      <DataTable
        data={rows}
        columns={columns}
        searchPlaceholder={t('Buscar alerta...')}
        emptyTitle={t('Sin alertas')}
        emptyDescription={t('No hay alertas para el filtro seleccionado.')}
      />
    </div>
  );
};

export default DevelopmentPermitAlerts;
